import {
    Box,
    Button,
    Chip,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    List,
    ListItem,
    Modal,
    OutlinedInput,
    Tab,
    Typography,
    Card,
    CardContent,
    CircularProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tooltip
} from "@mui/material"
import { useEffect, useState } from "react"
import DebugService from "../services/DebugService"
import {
    CONST_SITE_LABEL,
    customArrayOperations,
    firstLetterCapital,
    QUERY_LOG_ACCESS_LIST,
    setScopedItem,
    STORAGE_KEYS,
    SUBSCRIPTION_STATUS,
    toWholeStringByUnderscore,
    validatePassword,
    ALLOWED_PROVIDERS
} from "./common/Helper"
import './common/Styles.css'
import { MdVerifiedUser, MdOutlineWarning } from "react-icons/md";
import SubscriptionApiService from "../services/SubscriptionApiService"
import { format } from "date-fns"
import { CONST_ROUTE_PATH } from "../route/RouteConstants"
import { Visibility, VisibilityOff, Edit, Delete as DeleteIcon } from "@mui/icons-material"
import SnackbarNotification, { SNACKBAR_THEME } from "./common/SnackbarNotification"
import ForgotPasswordApiService from "../services/ForgotPasswordApiService"
import ConfirmModal from "./common/ConfirmModal"
import { useAuth } from "../state/AuthContext"
import { useScopedLocalStorage } from "../hooks/useScopedLocalStorage"
import { TabContext, TabList, TabPanel } from "@mui/lab"
import { useNavigate } from "react-router-dom"
import AIChatbotApiService from "../services/AIChatbotApiService"

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: '#fff',
    border: '1px solid #BDE0FF',
    borderRadius: '28px',
    boxShadow: '0px 4px 20px 0px #E3F0FC',
    p: 4,
    textAlign: 'center',
};

/**
 * Password field HOC
 * @param {*} props 
 * @returns 
 */
const CustomUserPasswordField = (props) => {
    const [showPassword, setShowPassword] = useState(false)

    return (
        <FormControl
            fullWidth
            size="small"
            required
            error={props['errorKey'] && props['errorKey'] !== undefined}
        >
            <InputLabel id="demo-simple-select-label" sx={{ color: '#202020' }}>{props['label']}</InputLabel>
            <OutlinedInput
                autoComplete="off"
                labelId="demo-simple-select-label"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                    <InputAdornment position="end">
                        <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(prev => !prev)}
                            edge="end"
                        >
                            {!showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    </InputAdornment>
                }
                sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#DCDBDD', // default border color
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#DCDBDD', // on hover
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#DCDBDD', // when focused
                    },
                }}
                {...props}
            />
            <FormHelperText>{props['errorKey'] && props['errorKey']}</FormHelperText>
        </FormControl>
    )
}

/**
 * Reset Password Modal Component
 * This is for logged in user
 * @param {*} props 
 * @returns 
 */
const ResetPasswordModal = (props) => {
    const [isOpen, setIsOpen] = useState(props['isOpen'] ?? false)
    const [isSaveDisabled, setIsSaveDisabled] = useState(true)
    const [errorMessages, setErrorMessages] = useState(null)
    const [userData, setUserData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    })

    /**
     * Modal close handler
     */
    const closeHandler = () => {
        props['isClose'](true)
        setIsOpen(false)
        setIsSaveDisabled(true)
        setErrorMessages(null)
    }

    /**
     * Save click handler
     */
    const onSaveClick = () => {
        setErrorMessages(null)
        let isValid = true

        if (!validatePassword(userData['currentPassword'])) {
            setErrorMessages(prev => {
                return {
                    ...prev,
                    currentPassword: 'Incorrect password'
                }
            })

            isValid = false
        }

        if (!validatePassword(userData['newPassword'])) {
            setErrorMessages(prev => {
                return {
                    ...prev,
                    newPassword: 'Incorrect password'
                }
            })

            isValid = false
        } else {
            if (userData['newPassword'] === userData['currentPassword']) {
                setErrorMessages(prev => {
                    return {
                        ...prev,
                        newPassword: 'Password should not be same as Current Password'
                    }
                })

                isValid = false
            }
        }

        if (!validatePassword(userData['confirmPassword'])) {
            setErrorMessages(prev => {
                return {
                    ...prev,
                    confirmPassword: 'Incorrect password'
                }
            })

            isValid = false
        } else {
            if (userData['newPassword'] !== userData['confirmPassword']) {
                setErrorMessages(prev => {
                    return {
                        ...prev,
                        confirmPassword: 'Password does not match with New Password'
                    }
                })

                isValid = false
            }
        }

        if (isValid) {
            props['saveClickHandler'](userData)
        }
        // closeHandler()
    }

    /**
     * call useEffect on user form change
     */
    useEffect(() => {
        DebugService.log(`Reset Password Modal: user form`, userData)

        if (
            userData['currentPassword'] !== '' &&
            userData['newPassword'] !== '' &&
            userData['confirmPassword'] !== ''
        ) {
            setIsSaveDisabled(false)

        } else {
            setIsSaveDisabled(true)
        }
    }, [userData])

    return (
        <div>
            <Modal
                className='confirm-modal'
                keepMounted
                open={isOpen}
                onClose={closeHandler}
                aria-labelledby="keep-mounted-modal-title"
                aria-describedby="keep-mounted-modal-description"
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: 'rgba(0, 0, 0, 0.65)',
                        },
                    },
                }}
            >
                <Box sx={style}>
                    <Typography id="keep-mounted-modal-title" variant="h4" color={'#012A4A'}>Reset Password</Typography>

                    {/* User Form */}
                    <Grid container spacing={1} style={{
                        margin: '1vw 0'
                    }}>
                        <Grid item xs={12}>
                            <CustomUserPasswordField
                                label="Current Password"
                                errorKey={errorMessages && errorMessages['currentPassword']}
                                defaultValue={(userData && userData['currentPassword']) ?? ''}
                                onChange={(e) => setUserData(prev => {
                                    return {
                                        ...prev,
                                        currentPassword: e['target']['value']
                                    }
                                })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <CustomUserPasswordField
                                label="New Password"
                                errorKey={errorMessages && errorMessages['newPassword']}
                                defaultValue={(userData && userData['newPassword']) ?? ''}
                                onChange={(e) => setUserData(prev => {
                                    return {
                                        ...prev,
                                        newPassword: e['target']['value']
                                    }
                                })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <CustomUserPasswordField
                                label="Confirm New Password"
                                errorKey={errorMessages && errorMessages['confirmPassword']}
                                defaultValue={(userData && userData['confirmPassword']) ?? ''}
                                onChange={(e) => setUserData(prev => {
                                    return {
                                        ...prev,
                                        confirmPassword: e['target']['value']
                                    }
                                })}
                            />
                        </Grid>

                    </Grid>

                    {/* Form Actions */}
                    <Grid container spacing={1}>
                        {/* Cancel Button */}
                        <Grid item xs={12} sm={6}>
                            <Button
                                variant='outlined'
                                onClick={() => closeHandler()}
                                className='modal-back-btn'
                            >Cancel</Button>
                        </Grid>

                        {/* Save Button */}
                        <Grid item xs={12} sm={6}>
                            <Button
                                disabled={isSaveDisabled}
                                variant='contained'
                                type="submit"
                                onClick={() => onSaveClick()}
                                className='modal-continue-btn'
                            >Save</Button>
                        </Grid>
                    </Grid>
                </Box>
            </Modal>
        </div>
    )
}

/**
 * My Profile component
 * @returns 
 */
export const MyProfile = () => {
    const [data, setData] = useState({})
    const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)
    const [showActivateNowConfirmModal, setShowActivateNowConfirmModal] = useState(false)
    const [showCancelSubscriptionConfirmModal, setShowCancelSubscriptionConfirmModal] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState(null)
    const [snackbarStatus, setSnackbarStatus] = useState(null)

    const [aiChatbotAccessKeys, setAiChatbotAccessKeys] = useState([]);
    const [fetchingAiChatbotAccessKeys, setFetchingAiChatbotAccessKeys] = useState(false);
    const [showAddEditAiChatbotAccessKeyModal, setShowAddEditAiChatbotAcessKeyModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedAiChatbotAccessKey, setSelectedAiChatbotAccessKey] = useState(null);
    const [formData, setFormData] = useState({
        accessKey: '',
        provider: ALLOWED_PROVIDERS[0].value,
    });
    const [showDeleteAiChatbotAcessKeyConfirm, setShowDeleteAiChatbotAcessKeyConfirm] = useState(false);

    const { selectedAccount } = useAuth()
    const { getScopedItem } = useScopedLocalStorage()
    const navigate = useNavigate()

    const fetchSubscriptionDetails = (selectedAccount) => {
        const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
        const parseToJson = JSON.parse(userDetails)

        if (parseToJson !== null && parseToJson !== undefined) {
            setData(prev => {
                return {
                    ...prev,
                    user: `${parseToJson?.['firstname']} ${parseToJson?.['lastname']}`,
                    email: parseToJson?.['email'],
                    role: firstLetterCapital(parseToJson?.['roleName']),
                    linkedAccountIds: (parseToJson?.['userAccounts'] ?? []).map(a => `${a['accountId']} / ${a['accountArn']}`),
                    emailVerified: parseToJson?.['emailVerified'],
                    userId: parseToJson?.['userId'],
                    userAccountId: selectedAccount ?? '',

                    // Reset subscription details
                    subscriptionStatus: '',
                    currentPlan: '',
                    recurringDate: null,
                    subscriptionAccess: '',
                    trialPeriod: 0,
                    validFrom: '',
                    validTill: '',
                    userSubscriptionID: ''
                }
            })

            SubscriptionApiService.getPurchasedPlanList({
                userId: parseToJson?.['userId'],
                selectedAccount: selectedAccount
            })
                .then(res => {
                    const data = (res['data'] ?? []).filter(s => s['accountId'] === selectedAccount)
                    DebugService.log(`My Profile component: Purchased Plan list`, data)

                    if (data.length > 0) {
                        const { latestRecord } = customArrayOperations(data, 'createdOn')

                        // Handle latest subscription status
                        if (latestRecord !== undefined || latestRecord !== null) {
                            setData(prev => {
                                return {
                                    ...prev,
                                    subscriptionStatus: latestRecord?.['status'],
                                    currentPlan: latestRecord['planCode'],
                                    recurringDate: latestRecord['recurringDate'] ? format(latestRecord['recurringDate'], 'dd-MM-yyyy HH:mm:ss') : null,
                                    subscriptionAccess: firstLetterCapital(toWholeStringByUnderscore(latestRecord['access'])),
                                    trialPeriod: latestRecord?.['payload']?.['product']?.['trialPeriod'] ?? 0,
                                    validFrom: latestRecord?.['currentStart'],
                                    validTill: latestRecord?.['currentEnd'],
                                    userSubscriptionID: latestRecord?.['userSubscriptionId']
                                }
                            })
                        } else {
                            DebugService.error(`My Profile component: Error caught while handling subscription based on latest purchased record`, latestRecord)
                        }
                    }
                })
                .catch(e => {
                    DebugService.error(`My Profile component: Error caught while fetching purchased plan list`, e)
                })
        }
    }

    /**
    * @description Fetches all AI chatbot access keys from the server and updates the local state.
    * @author Ashish Sharma
    */
    const fetchAIChatbotAccessKeys = async () => {
        setFetchingAiChatbotAccessKeys(true);
        try {
            const keys = await AIChatbotApiService.getAiChatbotAccessKeys();
            setAiChatbotAccessKeys(keys || []);
        } catch (err) {
            DebugService.error("Failed to fetch AI access keys", err);
            setSnackbarStatus('failed');
            setSnackbarMessage('Failed to fetch AI Access Keys');
        } finally {
            setFetchingAiChatbotAccessKeys(false);
        }
    };

    /**
     * Call useEffect on page load
     */
    useEffect(() => {
        document.title = `${CONST_SITE_LABEL} | My Profile`
        // fetchSubscriptionDetails()
    }, [])

    useEffect(() => {
        if (selectedAccount) {
            fetchSubscriptionDetails(selectedAccount)
        }
    }, [selectedAccount])

    useEffect(() => {
        fetchAIChatbotAccessKeys();
    }, []);

    /**
     * @description Reset password save handler
     * @param {*} formDetails 
     */
    const resetPasswordHandler = (formDetails) => {
        DebugService.log(`My Profile Component: Password form details`, formDetails)
        setSnackbarMessage(null)
        setSnackbarStatus(null)

        const requestBody = {
            password: formDetails['newPassword'],
            oldPassword: formDetails['currentPassword']
        }

        const userDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS)
        const parseToJson = JSON.parse(userDetails)

        if (parseToJson?.['userId']) {
            requestBody['userId'] = parseToJson['userId']
        }

        ForgotPasswordApiService.resetPassword(requestBody)
            .then(res => {
                const data = res['data'] ?? {}
                const error = res['error'] ?? {}

                DebugService.log(`My Profile Component: Reset Response`, res)

                if (error && error['message']) {
                    setShowResetPasswordModal(false)
                    setSnackbarStatus('failed')
                    setSnackbarMessage(error)
                } else {
                    setShowResetPasswordModal(false)
                    setSnackbarStatus('success')
                    setSnackbarMessage(data)
                }
            })
            .catch(err => {
                DebugService.error('My Profile Component: API Error caught', requestBody, err);
            })
            .finally(() => { })
    }

    const getSubscriptionStatusValue = (value) => {
        switch (value) {
            case SUBSCRIPTION_STATUS.TRIAL_ACTIVE: return 'Trial'
            case SUBSCRIPTION_STATUS.ACTIVE:
                if (!data['recurringDate']) {
                    return `Cancelled (Active till ${data['validTill']})`
                } else {
                    return firstLetterCapital(value ?? '')
                }
            default: return firstLetterCapital(value ?? '')
        }
    }

    /**
     * @description Instant activate subsciption handler
     */
    const activateSubscription = () => {
        if (data['userSubscriptionID']) {

            const requestBody = {
                userId: data['userId'],
                accountId: data['userAccountId'],
                userSubscriptionId: data['userSubscriptionID']
            }

            SubscriptionApiService.activateSubscription(requestBody)
                .then(res => {
                    DebugService.log(`My Profile Component: Activate subscription response`, res)

                    if (res['data']) {
                        setSnackbarStatus('success')
                        setSnackbarMessage(`${res['data']['message']}. Reload your page`)
                    } else {
                        if (res['error']['code'] === 525) {
                            setScopedItem(STORAGE_KEYS.CURRENT_ROUTE, CONST_ROUTE_PATH.PRICING)
                            navigate(CONST_ROUTE_PATH.PRICING)
                        } else {
                            setSnackbarStatus('failed')
                            setSnackbarMessage(res['error']['message'] ?? 'Subscription activation failed')
                        }
                    }
                })
                .catch(error => {
                    DebugService.log(`My Profile Component: Subscription activation failed`, error)
                    setSnackbarStatus('failed')
                    setSnackbarMessage('Something went wrong')
                })
        }
    }

    /**
     * @description Cancel Subscription
     */
    const cancelSubscription = () => {
        if (data['userSubscriptionID']) {

            const requestBody = {
                userId: data['userId'],
                accountId: data['userAccountId'],
                userSubscriptionId: data['userSubscriptionID']
            }

            SubscriptionApiService.cancelSubscription(requestBody)
                .then(res => {
                    DebugService.log(`My Profile Component: Cancel subscription response`, res)

                    if (res['data']) {
                        setSnackbarStatus('success')
                        setSnackbarMessage(`${res['data']}. Reload your page`)
                    } else {
                        setSnackbarStatus('failed')
                        setSnackbarMessage(res['error']['message'] ?? 'Subscription cancellation failed')
                    }
                })
                .catch(error => {
                    DebugService.log(`My Profile Component: Subscription cancellation failed`, error)
                    setSnackbarStatus('failed')
                    setSnackbarMessage('Something went wrong')
                })
        }
    }

    /**
     * @description Activate now confirmation handler
     * @param {*} isConfirm 
     */
    const activateNowConfirmationHandler = (isConfirm) => {
        if (isConfirm) {
            activateSubscription()
        }

        setShowActivateNowConfirmModal(false)
    }

    /**
     * @description Handle Cancel subscription when confirmed on modal
     * @param {*} isConfirm 
     */
    const cancelSubscriptionConfirmationHandler = (isConfirm) => {
        if (isConfirm) {
            cancelSubscription()
        }

        setShowCancelSubscriptionConfirmModal(false)
    }

    const accountSelectHandler = (accountDetails) => {
        const splitDetails = accountDetails.split('/').map(i => i.trim())
        fetchSubscriptionDetails(splitDetails[0])
    }

    const isAccessKeyInvalid = !formData.accessKey.trim();
    const isSaveDisabled =
        isAccessKeyInvalid || (!isEditMode && !formData.provider);

    /**
    * @description Prepares the UI for adding a new AI chatbot access key.
    * Resets edit mode, clears any selected key, initializes
    * the form with default values, and opens the add/edit modal.
    *
    * @author Ashish Sharma
    */
    const handleAddAiChatbotAccessKey = () => {
        setIsEditMode(false);
        setSelectedAiChatbotAccessKey(null);
        setFormData({ accessKey: '', provider: ALLOWED_PROVIDERS[0].value });
        setShowAddEditAiChatbotAcessKeyModal(true);
    };

    /**
    * @description Prepares the UI for editing an existing AI chatbot access key.
    * Enables edit mode, sets the selected key, populates the form
    * with the existing access key data, and opens the add/edit modal.
    * @param {Object} key - The selected AI chatbot access key to be edited.
    * @author Ashish Sharma
    */
    const handleEditAiChatbotAccessKey = (key) => {
        setIsEditMode(true);
        setSelectedAiChatbotAccessKey(key);
        setFormData({
            accessKey: key.accessKey,
            provider: key.provider,
        });
        setShowAddEditAiChatbotAcessKeyModal(true);
    };

    /**
    * @description Saves an AI chatbot access key.
    * If in edit mode, it updates the selected access key.
    * If not, it creates a new access key.
    * Performs validation before saving, shows appropriate
    * success or error notifications, closes the add/edit
    * modal on success, and refreshes the access key list.
    * @author Ashish Sharma
    */
    const handleSaveAiChatbotAccessKey = async () => {
        if (isAccessKeyInvalid) {
            setSnackbarStatus('failed');
            setSnackbarMessage('Access Key is required');
            return;
        }

        try {
            if (isEditMode) {
                await AIChatbotApiService.updateAiChatbotAccessKey({
                    userAiAccessKeyId: selectedAiChatbotAccessKey.userAiAccessKeyId,
                    accessKey: formData.accessKey.trim(),
                });
                setSnackbarMessage('AI Access Key updated successfully');
            } else {
                await AIChatbotApiService.addAiChatbotAccessKey({
                    accessKey: formData.accessKey.trim(),
                    provider: formData.provider,
                });
                setSnackbarMessage('AI Access Key added successfully');
            }

            setSnackbarStatus('success');
            setShowAddEditAiChatbotAcessKeyModal(false);
            fetchAIChatbotAccessKeys();
        } catch (err) {
            DebugService.error("Failed to save AI access key", err);
            setSnackbarStatus('failed');
            setSnackbarMessage('Failed to save AI Access Key');
        }
    };

    /**
    * @description Deletes the selected AI chatbot access key and updates the UI state.
    * On success, it shows a success notification, closes the confirmation
    * dialog, and refreshes the access key list. On failure, it logs the
    * error and displays a failure notification.
    * @author Ashish Sharma
    */
    const handleDeleteAiChatbotAccessKey = async () => {
        try {
            await AIChatbotApiService.deleteAiChatbotAccessKey(selectedAiChatbotAccessKey.userAiAccessKeyId);
            setSnackbarMessage('AI Access Key deleted successfully');
            setSnackbarStatus('success');
            setShowDeleteAiChatbotAcessKeyConfirm(false);
            fetchAIChatbotAccessKeys();
        } catch (err) {
            DebugService.error("Failed to delete AI access key", err);
            setSnackbarStatus('failed');
            setSnackbarMessage('Failed to delete AI Access Key');
        }
    };

    return (
        <Grid container spacing={4} className="my-profile-page">

            {/* User Grid */}
            <Grid item xs={12} md={6}>
                <TabContext value={'user-tab'} className="tab-context-custom">
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <TabList aria-label="User Information">
                            <Tab label={`User Information`} value={'user-tab'} className='tab user' />
                        </TabList>
                    </Box>
                    <TabPanel value={'user-tab'} className='tab-panel'>
                        <Grid container spacing={2} className="container">
                            <Grid item xs={12} className="row-user-icon">
                                <img src="./user-icon.svg" alt="User Icon" width={'65px'} height={'65px'} />
                            </Grid>
                            <Grid item xs={12} className="row-user-info">
                                <Typography variant="subtitle2">User Name</Typography>
                                <Typography variant="subtitle2" fontWeight={'600'}>{data?.['user']}</Typography>
                            </Grid>
                            <Grid item xs={12} className="row-user-info">
                                <Typography variant="subtitle2">Email</Typography>
                                <Typography variant="subtitle2" fontWeight={'600'}>{data?.['email']}&nbsp;
                                    {data?.['emailVerified']
                                        ? <MdVerifiedUser title="Verified Email" color={'#20BF55'} />
                                        : <MdOutlineWarning title="Pending for verification" color={'#e6b800'} />
                                    }
                                </Typography>
                            </Grid>
                            {/* <Grid item xs={12} className="row-user-info">
                                <Typography variant="subtitle2" color={'#425466'}>Password</Typography>
                                <Typography variant="subtitle2" fontWeight={'600'}>{data?.['user']}</Typography>
                            </Grid> */}
                            <Grid item xs={12} className="row-user-info">
                                <Typography variant="subtitle2">Role</Typography>
                                <Typography variant="subtitle2" fontWeight={'600'}>{data?.['role']}</Typography>
                            </Grid>
                            <Grid item xs={12} className="row-user-info linked-account">
                                <Typography variant="subtitle2">Linked Accounts</Typography>
                                <Typography variant="subtitle2" fontWeight={'600'}></Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <List dense>
                                    {(data?.['linkedAccountIds'] ?? []).map(a => (
                                        <ListItem className={`linked-account-item ${data?.['userAccountId'] && a.includes(data?.['userAccountId']) && 'selected'}`} title="Click to view subscription details">
                                            <Chip label={a} size="small" variant="outlined" className="linked-account-chip" onClick={() => accountSelectHandler(a)} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Grid>
                            <Grid item xs={12} container spacing={2}>
                                <Grid item xs={12} lg={2}></Grid>
                                <Grid item xs={12} sm={6} lg={4}>
                                    {QUERY_LOG_ACCESS_LIST.includes(data?.['email']) && (
                                        <Button
                                            variant='outlined'
                                            onClick={() => navigate(CONST_ROUTE_PATH.QUERY_LOGS)}
                                            className='queries-btn'
                                        >Go to Queries</Button>
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Button
                                        variant='contained'
                                        onClick={() => setShowResetPasswordModal(true)}
                                        className='reset-btn'
                                    >Reset Password</Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </TabPanel>
                </TabContext>
            </Grid>

            {/* Subscription Grid */}
            <Grid item xs={12} md={6}>
                <TabContext value={'subscription-tab'}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <TabList aria-label="Subscription">
                            <Tab label={`Subscription`} value={'subscription-tab'} className='tab subscription' />
                        </TabList>
                    </Box>
                    <TabPanel value={'subscription-tab'} className='tab-panel'>
                        <Grid container spacing={2} className="container">
                            <Grid item xs={12} className="row-user-icon">
                                <img src="./subscription-icon.svg" alt="User Icon" width={'65px'} height={'65px'} />
                            </Grid>
                            <Grid item xs={12} className="row-user-info">
                                <Typography variant="subtitle2">Account ID</Typography>
                                <Typography variant="subtitle2" fontWeight={'600'} >{data?.['userAccountId']}</Typography>
                            </Grid>
                            <Grid item xs={12} className="row-user-info">
                                <Typography variant="subtitle2">Subscription</Typography>
                                <Typography variant="subtitle2" fontWeight={'600'} className={`subscription-${getSubscriptionStatusValue(data?.['subscriptionStatus'])}`}>{getSubscriptionStatusValue(data?.['subscriptionStatus'])}</Typography>
                            </Grid>
                            <Grid item xs={12} className="row-user-info">
                                <Typography variant="subtitle2">Access</Typography>
                                <Typography variant="subtitle2" fontWeight={'600'}>{data?.['subscriptionAccess']}</Typography>
                            </Grid>
                            <Grid item xs={12} className="row-user-info">
                                <Typography variant="subtitle2">Current Plan</Typography>
                                <div>
                                    <Typography variant="subtitle2" fontWeight={'600'}>{data?.['currentPlan']}</Typography>
                                    {data?.['currentPlan'] && data?.['trialPeriod'] !== 0 &&
                                        <Typography variant="caption" color={'#595858'}>({data?.['trialPeriod']} days trial)</Typography>
                                    }
                                </div>
                            </Grid>
                            <Grid item xs={12} className="row-user-info">
                                <Typography variant="subtitle2">Valid From</Typography>
                                <Typography variant="subtitle2" fontWeight={'600'}>{data?.['validFrom']}</Typography>
                            </Grid>
                            <Grid item xs={12} className="row-user-info">
                                <Typography variant="subtitle2">Valid Till</Typography>
                                <div>
                                    <Typography variant="subtitle2" fontWeight={'600'}>{data?.['validTill']}</Typography>
                                    {data?.['recurringDate'] && <Typography variant="caption" color={'#595858'}>(Recurring On: {data?.['recurringDate']})</Typography>}
                                </div>
                            </Grid>
                            <Grid item xs={12} container spacing={2}>
                                <Grid item xs={12} lg={2}></Grid>
                                <Grid item xs={12} sm={6} lg={4}>
                                    {data?.['subscriptionStatus'] === SUBSCRIPTION_STATUS.TRIAL_ACTIVE &&
                                        <Button
                                            variant='outlined'
                                            onClick={() => setShowActivateNowConfirmModal(true)}
                                            className='queries-btn'
                                        >Activate Now</Button>
                                    }
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    {data?.['subscriptionAccess'] &&
                                        <Button
                                            variant='contained'
                                            onClick={() => setShowCancelSubscriptionConfirmModal(true)}
                                            className='reset-btn'
                                        >Cancel Subscription</Button>
                                    }
                                </Grid>
                            </Grid>
                        </Grid>
                    </TabPanel>
                </TabContext>
            </Grid>

            {/* AI Access Keys Section */}
            <Grid item xs={12}>
                <Paper elevation={3} sx={{ p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" mb={3}>
                        <Typography variant="h6">AI Access Keys</Typography>
                        <Stack direction="row" spacing={2}>
                            <Button variant="outlined" onClick={fetchAIChatbotAccessKeys}>
                                Refresh
                            </Button>
                            <Button variant="contained" onClick={handleAddAiChatbotAccessKey}>
                                Add Key
                            </Button>
                        </Stack>
                    </Stack>

                    {fetchingAiChatbotAccessKeys ? (
                        <Stack alignItems="center" py={6}>
                            <CircularProgress />
                        </Stack>
                    ) : aiChatbotAccessKeys.length === 0 ? (
                        <Typography align="center" py={6}>
                            No AI Access Keys found.
                        </Typography>
                    ) : (
                        <Stack spacing={2}>
                            {aiChatbotAccessKeys.map((key) => (
                                <Card key={key.userAiAccessKeyId} variant="outlined">
                                    <CardContent>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={2}>
                                                <Chip label={key.provider} />
                                            </Grid>

                                            <Grid item xs={12} sm={5}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <Tooltip title={key.accessKey}>
                                                        <Typography fontFamily="monospace">
                                                            {key.accessKey}
                                                        </Typography>
                                                    </Tooltip>
                                                </Stack>
                                            </Grid>

                                            <Grid item xs={6} sm={2} alignItems="center">
                                                <Typography variant="body2">
                                                    {format(new Date(key.createdAt), 'MMM d, yyyy')}
                                                </Typography>
                                            </Grid>

                                            <Grid item xs={12} sm={1} margin={'auto'}>
                                                <Stack direction="row">
                                                    <IconButton onClick={() => handleEditAiChatbotAccessKey(key)}>
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => {
                                                            setSelectedAiChatbotAccessKey(key);
                                                            setShowDeleteAiChatbotAcessKeyConfirm(true);
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                    )}
                </Paper>
            </Grid>

            {/* ADD / EDIT MODAL */}
            <Modal open={showAddEditAiChatbotAccessKeyModal} onClose={() => setShowAddEditAiChatbotAcessKeyModal(false)}>
                <Box className="ai-chatbot-modal">
                    <Typography variant="h6" mb={2}>
                        {isEditMode ? 'Edit AI Access Key' : 'Add AI Access Key'}
                    </Typography>

                    <FormControl
                        fullWidth
                        size="small"
                        sx={{ mb: 2 }}
                        error={isAccessKeyInvalid}
                    >
                        <InputLabel>Access Key</InputLabel>
                        <OutlinedInput
                            label="Access Key"
                            value={formData.accessKey}
                            onChange={(e) =>
                                setFormData(prev => ({
                                    ...prev,
                                    accessKey: e.target.value,
                                }))
                            }
                        />
                        {isAccessKeyInvalid && (
                            <Typography variant="caption" color="error">
                                Access Key is required
                            </Typography>
                        )}
                    </FormControl>

                    {!isEditMode && (
                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                            <InputLabel>Provider</InputLabel>
                            <Select
                                label="Provider"
                                value={formData.provider}
                                onChange={(e) =>
                                    setFormData(prev => ({
                                        ...prev,
                                        provider: e.target.value,
                                    }))
                                }
                            >
                                {ALLOWED_PROVIDERS.map(p => (
                                    <MenuItem key={p.value} value={p.value}>
                                        {p.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button onClick={() => setShowAddEditAiChatbotAcessKeyModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSaveAiChatbotAccessKey}
                            disabled={isSaveDisabled}
                        >
                            Save
                        </Button>
                    </Stack>
                </Box>
            </Modal>

            {/* DELETE CONFIRM */}
            {showDeleteAiChatbotAcessKeyConfirm && (
                <ConfirmModal
                    open={true}
                    headingLabel="Delete AI Access Key"
                    message={['Are you sure you want to delete this AI access key?']}
                    continueBtnLabel="Delete"
                    cancelBtnLabel="Cancel"
                    onActionTaken={(confirm) => {
                        confirm ? handleDeleteAiChatbotAccessKey() : setShowDeleteAiChatbotAcessKeyConfirm(false);
                    }}
                />
            )}

            {/* Reset Password Modal */}
            {showResetPasswordModal && <ResetPasswordModal
                isClose={(isCloseConfirm) => setShowResetPasswordModal(!isCloseConfirm)}
                isOpen={true}
                saveClickHandler={(formDetails) => resetPasswordHandler(formDetails)}
            />}

            {/* Snackbar Notification */}
            {snackbarStatus && <SnackbarNotification
                initialOpen={true}
                    duration={5000}
                message={snackbarMessage}
                actionButtonName={'Ok'}
                theme={snackbarStatus === 'success' ? SNACKBAR_THEME.GREEN : SNACKBAR_THEME.RED}
                yPosition={'top'}
                xPosition={'center'}
            />}

            {/* Confirm Modal */}
            {showActivateNowConfirmModal && <ConfirmModal
                open={true}
                headingLabel={'Activate Your Subscription!'}
                message={[
                    <p>By clicking the button below, you’ll gain full access to premium features, exclusive content, and personalized recommendations.</p>,
                    <br />
                ]}
                continueBtnLabel={'Proceed'}
                cancelBtnLabel={'Close'}
                onActionTaken={(isConfirm) => activateNowConfirmationHandler(isConfirm)}
            />}

            {/* Cancel subscription Confirm Modal */}
            {showCancelSubscriptionConfirmModal && <ConfirmModal
                open={true}
                headingLabel={'Cancel Your Subscription!'}
                message={[
                    <p>By clicking the button below, you’ll be revoked from your benefits</p>,
                    <br />
                ]}
                continueBtnLabel={'Proceed'}
                cancelBtnLabel={'Close'}
                onActionTaken={(isConfirm) => cancelSubscriptionConfirmationHandler(isConfirm)}
            />}
        </Grid>
    )
}